'use client'

import clsx from 'clsx'
import Image from 'next/image'
import { useFormatter, useTranslations } from 'next-intl'
import { useState } from 'react'

import { useClubWithdrawals } from '@/features/clubs/hooks/useClubMonetization'
import {
  type ClubFinance,
  microToUsdt
} from '@/features/clubs/lib/clubMonetization'

import S from './ClubMonetization.module.scss'
import WithdrawDialog from './WithdrawDialog'

interface WithdrawHistoryProps {
  finance?: ClubFinance
  canWithdraw: boolean
}

/** Withdraw history with the «Withdrawal request» button (design). */
const WithdrawHistory = ({ finance, canWithdraw }: WithdrawHistoryProps) => {
  const t = useTranslations('club.monetization')
  const format = useFormatter()
  const [open, setOpen] = useState(false)
  const { data: withdrawals, isError, refetch } = useClubWithdrawals()
  const canRequest = canWithdraw && Boolean(finance)

  return (
    <section className={S.history} aria-label={t('history')}>
      <header className={S.historyHeader}>
        <span className={S.cardTitle}>{t('history')}</span>
        <button
          type="button"
          className={S.primaryButton}
          onClick={() => setOpen(true)}
          disabled={!canRequest}
          title={canWithdraw ? undefined : t('locked')}
        >
          {t('request')}
        </button>
      </header>
      {!canWithdraw ? <p className={S.lockedNote}>{t('locked')}</p> : null}

      {isError ? (
        <div className={S.emptyState}>
          <p className={S.emptyTitle}>{t('historyError')}</p>
          <button type="button" className={S.retry} onClick={() => refetch()}>
            {t('retry')}
          </button>
        </div>
      ) : withdrawals && withdrawals.length === 0 ? (
        <div className={S.emptyState}>
          <Image
            src="/assets/club-profile/operations-empty.png"
            alt=""
            width={96}
            height={96}
          />
          <p className={S.emptyTitle}>{t('noOperations')}</p>
        </div>
      ) : (
        <ul className={S.historyList} aria-busy={!withdrawals}>
          {(withdrawals ?? []).map(item => (
            <li key={item.id} className={S.historyRow}>
              <div className={S.historyMain}>
                <span className={S.rowTitle}>{t('rowTitle')}</span>
                <span className={S.rowSub}>
                  {format.dateTime(new Date(item.created_at), {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </span>
              </div>
              <span className={clsx(S.status, S[`status_${item.status}`])}>
                {t(`status.${item.status}`)}
              </span>
              <div className={S.historyAmount}>
                <span className={S.rowTitle}>
                  {format.number(item.amount_ap)} AP
                </span>
                {item.usdt_micro > 0 ? (
                  <span className={S.rowSub}>
                    ≈{' '}
                    {format.number(microToUsdt(item.usdt_micro), {
                      maximumFractionDigits: 2
                    })}{' '}
                    USDT
                  </span>
                ) : null}
              </div>
            </li>
          ))}
        </ul>
      )}

      {finance ? (
        <WithdrawDialog
          open={open}
          onClose={() => setOpen(false)}
          finance={finance}
        />
      ) : null}
    </section>
  )
}

export default WithdrawHistory
